import React, {
  useEffect, useState, useRef, useCallback, useMemo, RefObject, ReactElement,
} from 'react';
import Widget from '../widget/widget.component';
import LinkPlain from '../link-plain/link-plain.component';
import LocationMapMarker from '../location-map-marker/location-map-marker.component';
import GoogleMapService from '../../services/google-map-service';
import FormatService from '../../services/format-service';
import useStylesLocationMap from './location-map.styles';
import { SIGN_DOT } from '../../constants/system/signs';
import './location-map.scss';

const LocationMap: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const LATITUDE_DEFAULT: number = 40.7131;
  const LONGITUDE_DEFAULT: number = -74.0003;
  const DEFAULT_ZOOM: number = 14;

  const {
    className,
    defaultColor,
    locationItemList,
    colorIdList,
  } = props;

  const { classes } = useStylesLocationMap();
  const containerLocationMapRef: RefObject<HTMLDivElement> | null = useRef(null);

  const [currentColorIdList, setCurrentColorIdList]: [number[], Function] = useState([]);
  const [currentLocationItemIdList, setCurrentLocationItemIdList]: [number[], Function] = useState([]);

  const locationItemIdList: number[] = useMemo((): number[] => (
    locationItemList && locationItemList.length
      ? locationItemList.map((locationItem: LocationMapItem): number => locationItem.id) : []
  ), [locationItemList]);

  const isSingleLocationView: boolean = locationItemList && locationItemList.length === 1;
  const firstLocationItem: LocationMapItem = (locationItemList && locationItemList[0]) || null;

  const drawMap: Function = useCallback((): void => {
    if (containerLocationMapRef.current) {
      GoogleMapService.drawMap(
        containerLocationMapRef.current,
        {
          lat: isSingleLocationView && firstLocationItem && firstLocationItem.latitude && firstLocationItem.longitude
            ? firstLocationItem.latitude : LATITUDE_DEFAULT,
          lng: isSingleLocationView && firstLocationItem.latitude && firstLocationItem.longitude
            ? firstLocationItem.longitude : LONGITUDE_DEFAULT,
        },
        DEFAULT_ZOOM,
        locationItemList && locationItemList.length
          ? locationItemList.map((locationItem: LocationMapItem): GoogleMapsMarkerInput | null => (
            locationItem.latitude && locationItem.longitude ? ({
              position: {
                lat: locationItem.latitude,
                lng: locationItem.longitude,
              },
              content: FormatService.formatComponentToHtml(
                <LinkPlain withNewWindow link={locationItem.link}>
                  <LocationMapMarker
                    title={locationItem.title}
                    amount={locationItem.amount}
                    imageLink={locationItem.imageLink}
                  />
                </LinkPlain>,
              ),
              color: locationItem.color || defaultColor,
            }) : null
          )).filter((marker: GoogleMapsMarkerInput | null): boolean => !!marker) : [],
        isSingleLocationView,
      );
    }
  }, [locationItemList, containerLocationMapRef, isSingleLocationView, defaultColor]);

  useEffect((): void => {
    if (locationItemIdList && locationItemIdList.join(SIGN_DOT) !== currentLocationItemIdList.join(SIGN_DOT)) {
      drawMap();
      setCurrentLocationItemIdList(locationItemIdList);
    }
  }, [locationItemIdList]);

  useEffect((): void => {
    if (colorIdList && currentColorIdList.join(SIGN_DOT) !== colorIdList.join(SIGN_DOT)) {
      drawMap();
      setCurrentColorIdList(colorIdList);
    }
  }, [colorIdList]);

  return (
    <Widget isFullWidth isFullHeight withNoHeader>
      <div
        ref={containerLocationMapRef}
        className={FormatService.formatClassNames(classes.locationMap, className)}
      />
    </Widget>
  );
});

export default LocationMap;
